import { useEffect, useMemo, useState, type ReactNode } from "react";
import { openWithCacheBuster } from "./cacheBuster";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Link2,
  Pencil,
} from "lucide-react";
import type {
  InternalLinkAnchorCandidate,
  PageIssue,
  OrphanPageFix,
  ProposedFix,
} from "@/types/technicalAudit";

interface InternalLinkSuggestionCardProps {
  issue: PageIssue;
  /**
   * Called when the specialist picks a source page or edits the anchor text,
   * with the value the backend applies: insert an internal link FROM the
   * chosen source page TO the under-linked target page using the anchor text.
   * The drawer stores it as `editedValue` and sends it as `approvedValue`.
   *
   * Shared by orphan_page and low_internal_links — both add a contextual
   * internal link to an under-linked target page and emit the identical shape.
   */
  onEdit?: (value: ProposedFix) => void;
}

type RecommendedSource = {
  url: string;
  title: string;
  suggested_anchor_text?: string | null;
  match_score?: number;
  post_type?: string;
  snippet?: string | null;
  /** True when `snippet` is page context, not a verified anchor placement. */
  snippet_unverified?: boolean;
  insertion_mode?: string;
  original_sentence?: string;
  source_sentence?: string;
  rewritten_sentence?: string;
  has_anchor?: boolean;
  manual_review_required?: boolean;
  needs_anchor_selection?: boolean;
  source?: string;
  borderline?: boolean;
  fit_score?: number | null;
  confidence?: number | null;
  llm_selected?: boolean;
  llm_candidate_id?: string;
  llm_confidence?: number | null;
  llm_reasoning?: string;
  anchor_selection_status?: string;
  anchor_candidates?: InternalLinkAnchorCandidate[];
  placement_candidates?: InternalLinkAnchorCandidate[];
  selected_anchor_candidate?: InternalLinkAnchorCandidate | null;
};

// How many sources are pre-selected (and the max the apply will insert)
// when the backend doesn't supply an explicit `source_pages` default.
const DEFAULT_SPREAD = 3;
const MAX_SPREAD = 5;

// Render `snippet` with every case-insensitive occurrence of `anchor` made bold,
// so the specialist can see exactly where the new internal link lands. Returns a
// list of React fragments (plain text + emphasized anchor spans).
function highlightAnchor(snippet: string, anchor: string): ReactNode {
  const trimmedAnchor = anchor.trim();
  if (!trimmedAnchor) return <>{snippet}</>;
  // Escape regex metacharacters so the anchor is matched literally.
  const escaped = trimmedAnchor.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = snippet.split(new RegExp(`(${escaped})`, "gi"));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === trimmedAnchor.toLowerCase() ? (
          <strong key={i} className="font-semibold text-teal-dark">
            {part}
          </strong>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

// Parse a fix value (a parsed object or a JSON string) leniently so the card
// never crashes on malformed data.
function parseFixValue(raw: unknown): OrphanPageFix | null {
  if (!raw) return null;
  if (typeof raw === "object" && !Array.isArray(raw))
    return raw as OrphanPageFix;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? (parsed as OrphanPageFix)
        : null;
    } catch {
      return null;
    }
  }
  return null;
}

function recommendedSourcesOf(fix: OrphanPageFix | null): RecommendedSource[] {
  if (!fix || !Array.isArray(fix.recommended_source_pages)) return [];
  return fix.recommended_source_pages.filter((s): s is RecommendedSource =>
    Boolean(s && typeof s.url === "string" && s.url),
  );
}

function hasPlacementPreview(source: RecommendedSource): boolean {
  return (
    (typeof source.snippet === "string" && source.snippet.trim().length > 0) ||
    Boolean(topVerifiedCandidateOf(source))
  );
}

function anchorTextOf(
  candidate: InternalLinkAnchorCandidate | null | undefined,
): string {
  return (
    candidate?.anchor_text ||
    candidate?.suggested_anchor_text ||
    ""
  ).trim();
}

function snippetOf(
  candidate: InternalLinkAnchorCandidate | null | undefined,
): string {
  return typeof candidate?.snippet === "string" ? candidate.snippet.trim() : "";
}

function candidateInsertionMode(
  candidate: InternalLinkAnchorCandidate | null | undefined,
): string {
  return typeof candidate?.insertion_mode === "string"
    ? candidate.insertion_mode
    : "";
}

function originalSentenceOf(
  candidate: InternalLinkAnchorCandidate | null | undefined,
): string {
  return (
    candidate?.original_sentence ||
    candidate?.source_sentence ||
    ""
  ).trim();
}

function rewrittenSentenceOf(
  candidate: InternalLinkAnchorCandidate | null | undefined,
): string {
  return (candidate?.rewritten_sentence || "").trim();
}

function isSentenceRewriteCandidate(
  candidate: InternalLinkAnchorCandidate | null | undefined,
): boolean {
  return candidateInsertionMode(candidate) === "sentence_rewrite";
}

function candidatesOf(
  source: RecommendedSource,
): InternalLinkAnchorCandidate[] {
  const raw = Array.isArray(source.anchor_candidates)
    ? source.anchor_candidates
    : Array.isArray(source.placement_candidates)
      ? source.placement_candidates
      : [];
  return raw.filter((candidate): candidate is InternalLinkAnchorCandidate =>
    Boolean(candidate && anchorTextOf(candidate) && snippetOf(candidate)),
  );
}

function verifiedCandidatesOf(
  source: RecommendedSource,
): InternalLinkAnchorCandidate[] {
  return candidatesOf(source).filter(
    (candidate) =>
      candidate.verified !== false && candidate.has_anchor !== false,
  );
}

function topVerifiedCandidateOf(
  source: RecommendedSource,
): InternalLinkAnchorCandidate | null {
  return (
    source.selected_anchor_candidate || verifiedCandidatesOf(source)[0] || null
  );
}

function sourceNeedsAnchorSelection(source: RecommendedSource): boolean {
  if (
    source.manual_review_required ||
    source.needs_anchor_selection ||
    source.anchor_selection_status === "needs_anchor_selection" ||
    source.source === "ranked_fallback" ||
    source.has_anchor === false
  ) {
    return true;
  }
  return !hasPlacementPreview(source);
}

function sourceIsApplyReady(source: RecommendedSource): boolean {
  return !sourceNeedsAnchorSelection(source);
}

function displayAnchorOf(source: RecommendedSource): string {
  return (
    source.suggested_anchor_text ||
    anchorTextOf(topVerifiedCandidateOf(source)) ||
    ""
  ).trim();
}

function displaySnippetOf(source: RecommendedSource): string {
  const sourceSnippet =
    typeof source.snippet === "string" ? source.snippet.trim() : "";
  return sourceSnippet || snippetOf(topVerifiedCandidateOf(source));
}

function InlineUrlLink({ label, url }: { label: string; url?: string | null }) {
  if (!url) {
    return (
      <span className="min-w-0 break-all font-mono">{label}: unknown</span>
    );
  }

  return (
    <span className="min-w-0 break-all font-mono">
      {label}:{" "}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => {
          event.stopPropagation();
          openWithCacheBuster(event, url);
        }}
        className="inline-flex items-center gap-1 text-coral hover:text-coral-hover hover:underline"
      >
        {url}
        <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
      </a>
    </span>
  );
}

function buildInitialSelection(
  sg: OrphanPageFix | null,
  approved: OrphanPageFix | null,
  list: RecommendedSource[],
) {
  const known = new Set(list.map((s) => s.url));
  const inList = (u: unknown): u is string =>
    typeof u === "string" && known.has(u);

  const fromApproved = [
    ...(Array.isArray(approved?.source_pages)
      ? approved.source_pages.map((p) => p?.source_page_url)
      : []),
    ...(typeof approved?.source_page_url === "string"
      ? [approved.source_page_url]
      : []),
  ].filter(inList);
  const readyApproved = fromApproved.filter((url) => {
    const source = list.find((s) => s.url === url);
    return source ? sourceIsApplyReady(source) : false;
  });
  const fromDefault = Array.isArray(sg?.source_pages)
    ? sg.source_pages.map((p) => p?.source_page_url).filter(inList)
    : [];
  const readyDefault = fromDefault.filter((url) => {
    const source = list.find((s) => s.url === url);
    return source ? sourceIsApplyReady(source) : false;
  });
  const readyFallback = list
    .filter(sourceIsApplyReady)
    .slice(0, Math.min(DEFAULT_SPREAD, list.length))
    .map((s) => s.url);
  const checkedUrls = (
    readyApproved.length > 0
      ? readyApproved
      : readyDefault.length > 0
        ? readyDefault
        : readyFallback
  ).slice(0, MAX_SPREAD);

  // The override defaults to EMPTY so each source uses its OWN per-source anchor.
  // Only restore a prior override if every approved source shared one explicit
  // anchor that does NOT match its blog's suggested anchor (i.e. a real override).
  const approvedSources = Array.isArray(approved?.source_pages)
    ? approved.source_pages
    : [];
  const sharedApprovedAnchor =
    approvedSources.length > 0 &&
    approvedSources.every(
      (p) =>
        typeof p?.anchor_text === "string" &&
        p.anchor_text === approvedSources[0]?.anchor_text,
    )
      ? (approvedSources[0]?.anchor_text ?? "")
      : "";
  const isRealOverride =
    sharedApprovedAnchor.length > 0 &&
    approvedSources.some((p) => {
      const source = list.find((s) => s.url === p?.source_page_url);
      const own = source ? displayAnchorOf(source) : "";
      return own && own !== sharedApprovedAnchor;
    });

  return {
    checkedUrls,
    anchorOverride: isRealOverride ? sharedApprovedAnchor : "",
  };
}

/**
 * Editable multi-source picker for orphan_page and low_internal_links. The inbound
 * internal link is SPREAD across several distinct sources — one link per selected
 * source — and each source uses its OWN `suggested_anchor_text` (a phrase that exists
 * in that source). An optional override input replaces every source's anchor when set.
 * Reads recommended_source_pages / source_pages generically and emits
 * onEdit({ source_pages: [{ source_page_url, anchor_text, title, snippet }],
 * source_page_url, anchor_text }) where the top-level fields mirror the first
 * selected blog for back-compat.
 */
export function InternalLinkSuggestionCard({
  issue,
  onEdit,
}: InternalLinkSuggestionCardProps) {
  const sourceLabelPlural = "source pages";
  const suggestionValue = issue.proposed_fix ?? issue.proposed_value;
  const approvedValue = issue.approved_value;
  const fix = useMemo(() => parseFixValue(suggestionValue), [suggestionValue]);
  const approvedFix = useMemo(
    () => parseFixValue(approvedValue),
    [approvedValue],
  );
  const sources = useMemo(() => recommendedSourcesOf(fix), [fix]);
  const hasSourceSuggestion = sources.length > 0;
  const reviewPlan = Array.isArray(fix?.suggested_internal_link_plan)
    ? fix.suggested_internal_link_plan.filter(
        (item): item is string =>
          typeof item === "string" && item.trim() !== "",
      )
    : [];
  const hasReviewOnlyRecommendation = Boolean(
    fix &&
      !hasSourceSuggestion &&
      (fix.recommendation ||
        fix.reasoning ||
        fix.target_link_strategy ||
        reviewPlan.length > 0),
  );
  const hasSuggestion = hasSourceSuggestion || hasReviewOnlyRecommendation;
  const draftKey = `${issue.id}:${JSON.stringify(suggestionValue ?? "")}:${JSON.stringify(approvedValue ?? "")}`;
  const initialSelection = useMemo(
    () => buildInitialSelection(fix, approvedFix, sources),
    [fix, approvedFix, sources],
  );

  // URLs of the sources the link is spread across (one inbound link per source).
  const [draft, setDraft] = useState<{
    key: string;
    checkedUrls: string[];
    anchorOverride: string;
  } | null>(null);
  // Anchor picked per source by clicking one of its suggested phrases. Beats the
  // shared override when set: rows that need an anchor can each get their own
  // without forcing one phrase onto every source.
  const [pickedAnchors, setPickedAnchors] = useState<Record<string, string>>({});
  // Choosing an anchor for a source means you intend to use that source, so
  // picking one also selects the row. This is deliberate: the chips sit inside
  // the row's <label>, so a chip click also toggles the checkbox — forcing the
  // row selected here keeps that from silently deselecting it.
  const pickAnchor = (url: string, anchor: string) => {
    setPickedAnchors((prev) => ({
      ...prev,
      [url]: prev[url] === anchor ? "" : anchor,
    }));
    setDraft((prev) => {
      const current =
        prev?.key === draftKey ? prev.checkedUrls : initialSelection.checkedUrls;
      if (current.includes(url) || current.length >= MAX_SPREAD) {
        return { key: draftKey, checkedUrls: current, anchorOverride };
      }
      return { key: draftKey, checkedUrls: [...current, url], anchorOverride };
    });
  };
  const checkedUrls =
    draft?.key === draftKey ? draft.checkedUrls : initialSelection.checkedUrls;
  // Optional override anchor applied to EVERY selected source. When empty, each
  // source uses its OWN `suggested_anchor_text` (a phrase that exists in that source).
  const anchorOverride =
    draft?.key === draftKey
      ? draft.anchorOverride
      : initialSelection.anchorOverride;

  // Push the draft up so Approve/Apply send exactly what the specialist sees.
  // Each selected source carries its OWN anchor (`suggested_anchor_text`, a phrase
  // that exists in THAT source) unless the optional override is set, which replaces
  // every source's anchor. Emitting an empty `source_pages` array keeps the fix from
  // counting as "meaningful", which disables Approve/Apply until ≥1 source is
  // checked — the override input is NOT required. Re-reads the sources from the
  // issue so the effect doesn't depend on a per-render array reference (no loop).
  useEffect(() => {
    if (!hasSourceSuggestion) return;
    // Emit every CHECKED source, including ones still awaiting an anchor.
    // Dropping them here made ticking such a row a no-op even once the checkbox
    // allowed it. Rows without an anchor emit anchor_text: "" and the drawer's
    // needsInternalLinkSourceSelection keeps Apply disabled until an override
    // or a picked candidate fills it in — the gate lives there, not here.
    const checkedSources = sources.filter((s) => checkedUrls.includes(s.url));
    const override = anchorOverride.trim();
    const sourcePages = checkedSources.map((s) => {
      const topCandidate = topVerifiedCandidateOf(s);
      const needsAnchorSelection = sourceNeedsAnchorSelection(s);
      const usesSentenceRewrite = isSentenceRewriteCandidate(topCandidate);
      const picked = (pickedAnchors[s.url] || "").trim();
      const anchorText = usesSentenceRewrite
        ? displayAnchorOf(s)
        : picked || override || (needsAnchorSelection ? "" : displayAnchorOf(s));
      // True only when the ANCHOR CAME FROM THE OPERATOR on a row that was
      // waiting for one — not when the backend merely suggested a phrase.
      const operatorSuppliedAnchor =
        needsAnchorSelection && !usesSentenceRewrite && Boolean(picked || override);
      return {
        source_page_url: s.url,
        anchor_text: anchorText,
        title: s.title,
        snippet: needsAnchorSelection
          ? (s.snippet ?? null)
          : displaySnippetOf(s) || null,
        insertion_mode:
          topCandidate?.insertion_mode || s.insertion_mode || undefined,
        original_sentence:
          topCandidate?.original_sentence ||
          topCandidate?.source_sentence ||
          s.original_sentence ||
          s.source_sentence ||
          undefined,
        rewritten_sentence:
          topCandidate?.rewritten_sentence || s.rewritten_sentence || undefined,
        post_type: s.post_type,
        match_score: s.match_score,
        has_anchor: s.has_anchor,
        // An operator-supplied anchor (picked chip or typed override) clears
        // the review flags for THIS emission: the anchor the row was waiting
        // for now exists. The marker is what lets the drawer's sanitization
        // keep the row (a backend-*suggested* anchor on an unverified row is
        // still stripped — see isManualReviewInternalLinkSource).
        manual_review_required: operatorSuppliedAnchor
          ? false
          : s.manual_review_required,
        needs_anchor_selection: operatorSuppliedAnchor
          ? false
          : needsAnchorSelection,
        anchor_selection_status: operatorSuppliedAnchor
          ? "operator_selected"
          : s.anchor_selection_status,
        source: s.source,
        borderline: s.borderline,
        fit_score: s.fit_score,
        confidence: s.confidence,
        suggested_anchor_text: s.suggested_anchor_text || undefined,
        anchor_candidates: s.anchor_candidates,
        placement_candidates: s.placement_candidates,
        selected_anchor_candidate: topCandidate,
      };
    });
    // Back-compat top-level fields point at the FIRST selected blog so the
    // drawer's "meaningful fix value" check passes when ≥1 blog is checked.
    onEdit?.({
      source_pages: sourcePages,
      source_page_url: sourcePages[0]?.source_page_url,
      anchor_text: sourcePages[0]?.anchor_text,
    } as ProposedFix);
  }, [checkedUrls, anchorOverride, pickedAnchors, hasSourceSuggestion, sources, onEdit]);

  // Toggle a blog in/out of the spread (capped at MAX_SPREAD selected).
  const toggleSource = (url: string) => {
    setDraft((prev) => {
      const current =
        prev?.key === draftKey
          ? prev.checkedUrls
          : initialSelection.checkedUrls;
      const next = current.includes(url)
        ? current.filter((u) => u !== url)
        : current.length >= MAX_SPREAD
          ? current
          : [...current, url];
      return { key: draftKey, checkedUrls: next, anchorOverride };
    });
  };

  const targetUrl = fix?.orphan_page_url || issue.page_url;
  const previewableCount = sources.filter(hasPlacementPreview).length;
  const hasMissingPreviews = previewableCount < sources.length;
  const selectedCount = sources.filter(
    (s) => checkedUrls.includes(s.url) && sourceIsApplyReady(s),
  ).length;

  if (!hasSuggestion) {
    return (
      <Card className="border-border shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-foreground">
            Recommended Link Fix
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-sm text-muted-foreground">
            Click <span className="font-medium">Generate fix</span> above to
            find relevant {sourceLabelPlural}, then select the ones to spread
            the inbound link across and edit the anchor text.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!hasSourceSuggestion && hasReviewOnlyRecommendation) {
    return (
      <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-amber-900 dark:text-amber-300 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-card flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-amber-700 dark:text-amber-400" />
            </div>
            Recommended Link Fix
            <Badge
              variant="outline"
              className="ml-auto bg-card text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800"
            >
              review only
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          <div className="grid gap-1 text-[11px] text-muted-foreground">
            <InlineUrlLink label="link to" url={targetUrl} />
            {fix?.target_link_strategy && (
              <span className="font-medium text-amber-900 dark:text-amber-300">
                strategy: {fix.target_link_strategy.replace(/_/g, " ")}
              </span>
            )}
          </div>
          {fix?.recommendation && (
            <p className="text-sm font-medium text-amber-950">
              {fix.recommendation}
            </p>
          )}
          {fix?.reasoning && (
            <p className="text-sm leading-relaxed text-amber-900 dark:text-amber-300">
              {fix.reasoning}
            </p>
          )}
          {reviewPlan.length > 0 && (
            <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-card px-3 py-2">
              <div className="text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:text-amber-300">
                Plan
              </div>
              <ul className="mt-1 space-y-1 text-xs leading-relaxed text-foreground">
                {reviewPlan.map((item, index) => (
                  <li key={`${item}:${index}`}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-teal/30 shadow-sm bg-teal-light/40">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold text-teal-dark flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-teal-light flex items-center justify-center">
            <Link2 className="w-4 h-4 text-teal-dark" />
          </div>
          Add internal link
          <Badge
            variant="outline"
            className="ml-auto bg-teal-light text-teal-dark border-teal/30"
          >
            editable
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-4">
        {hasMissingPreviews && (
          <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-900 dark:text-amber-300">
            Missing placement preview
          </div>
        )}

        <>
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] font-medium uppercase tracking-wide text-teal-dark">
                Link from (pick {sourceLabelPlural})
              </div>
              <span className="text-[11px] text-muted-foreground">
                {selectedCount} of {Math.min(sources.length, MAX_SPREAD)}{" "}
                selected
              </span>
            </div>
            <div className="space-y-2">
              {sources.map((source) => {
                // Selection and apply-readiness are DIFFERENT gates. A source
                // that still needs an anchor chosen is a legitimate choice —
                // its own helper text says "select only if you plan to choose
                // the anchor during review" — so it must be tickable. Apply
                // stays blocked until every selected source carries an anchor
                // (drawer: needsInternalLinkSourceSelection), which is where
                // that safety belongs. Previously canSelect gated the checkbox
                // itself, so a backfilled suggestion list was unusable: three
                // options, none selectable, Apply permanently dead.
                const selected = checkedUrls.includes(source.url);
                const atCap = !selected && selectedCount >= MAX_SPREAD;
                return (
                  <label
                    key={source.url}
                    className={`block rounded-md border px-3 py-2 transition-colors ${
                      selected
                        ? "border-teal bg-teal-light/70 cursor-pointer"
                        : atCap
                          ? "border-border bg-card opacity-50 cursor-not-allowed"
                          : // Needs-anchor rows are FULL opacity: they are real,
                          // selectable recommendations. The "Needs anchor
                          // selection" badge already says what is outstanding;
                          // greying them out only read as "broken/unavailable".
                          "border-border bg-card hover:bg-teal-light/30 cursor-pointer"
                    }`}
                  >
                    <div className="flex min-w-0 items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-1 shrink-0 accent-teal"
                        checked={selected}
                        disabled={atCap}
                        onChange={() => toggleSource(source.url)}
                      />
                      <div className="min-w-0 flex-1">
                        {(() => {
                          const needsAnchorSelection =
                            sourceNeedsAnchorSelection(source);
                          const topCandidate = topVerifiedCandidateOf(source);
                          const usesSentenceRewrite =
                            isSentenceRewriteCandidate(topCandidate);
                          const candidates = candidatesOf(source);
                          const rowAnchor = usesSentenceRewrite
                            ? displayAnchorOf(source)
                            : anchorOverride.trim() ||
                              (needsAnchorSelection
                                ? ""
                                : displayAnchorOf(source));
                          const rowSnippet = displaySnippetOf(source);
                          return (
                            <div className="min-w-0 space-y-2">
                              <div className="min-w-0">
                                <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                                  <span className="truncate text-sm font-medium text-foreground">
                                    {source.title || source.url}
                                  </span>
                                  {(source.post_type === "post" ||
                                    source.post_type === "page") && (
                                    <Badge
                                      variant="outline"
                                      className="shrink-0 bg-teal-light text-teal-dark border-teal/30"
                                    >
                                      {source.post_type === "page"
                                        ? "Page"
                                        : "Post"}
                                    </Badge>
                                  )}
                                  {needsAnchorSelection ? (
                                    <Badge
                                      variant="outline"
                                      className="shrink-0 bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800 inline-flex items-center gap-1"
                                    >
                                      <AlertTriangle
                                        className="h-3 w-3"
                                        aria-hidden="true"
                                      />
                                      Needs anchor selection
                                    </Badge>
                                  ) : (
                                    <Badge
                                      variant="outline"
                                      className="shrink-0 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800 inline-flex items-center gap-1"
                                    >
                                      <CheckCircle2
                                        className="h-3 w-3"
                                        aria-hidden="true"
                                      />
                                      Verified anchor
                                    </Badge>
                                  )}
                                  {isSentenceRewriteCandidate(topCandidate) && (
                                    <Badge
                                      variant="outline"
                                      className="shrink-0 bg-card text-teal-dark border-teal/30"
                                    >
                                      Sentence rewrite
                                    </Badge>
                                  )}
                                  {typeof source.match_score === "number" && (
                                    <Badge
                                      variant="outline"
                                      className="shrink-0 bg-card text-muted-foreground border-border"
                                    >
                                      score {source.match_score}
                                    </Badge>
                                  )}
                                </div>
                                <div className="mt-1 grid gap-1 text-[11px] text-muted-foreground">
                                  <InlineUrlLink
                                    label="source"
                                    url={source.url}
                                  />
                                  <InlineUrlLink
                                    label="link to"
                                    url={targetUrl}
                                  />
                                  {rowAnchor ? (
                                    <span className="font-medium text-teal-dark">
                                      anchor: {rowAnchor}
                                    </span>
                                  ) : source.suggested_anchor_text ? (
                                    <span className="font-medium text-amber-800 dark:text-amber-300">
                                      suggested anchor needs review:{" "}
                                      {source.suggested_anchor_text}
                                    </span>
                                  ) : null}
                                  {topCandidate && !needsAnchorSelection && (
                                    <span className="text-muted-foreground">
                                      top candidate:{" "}
                                      {anchorTextOf(topCandidate)}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="w-full rounded-md bg-card/80 border border-border px-2.5 py-2">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                                    {needsAnchorSelection
                                      ? "Placement review"
                                      : "Verified placement"}
                                  </div>
                                  {candidates.length > 1 && (
                                    <span className="text-[10px] text-muted-foreground">
                                      {candidates.length} candidates
                                    </span>
                                  )}
                                </div>
                                <div className="mt-1 break-words text-xs leading-relaxed text-foreground">
                                  {isSentenceRewriteCandidate(topCandidate) &&
                                  originalSentenceOf(topCandidate) &&
                                  rewrittenSentenceOf(topCandidate) ? (
                                    <div className="space-y-1">
                                      <div className="text-muted-foreground">
                                        {originalSentenceOf(topCandidate)}
                                      </div>
                                      <div>
                                        {highlightAnchor(
                                          rewrittenSentenceOf(topCandidate),
                                          rowAnchor,
                                        )}
                                      </div>
                                    </div>
                                  ) : rowSnippet && rowAnchor ? (
                                    highlightAnchor(rowSnippet, rowAnchor)
                                  ) : rowSnippet ? (
                                    // Context from the source page: no anchor is
                                    // chosen yet, so nothing is highlighted, but
                                    // the operator can read what the page says
                                    // and pick a phrase from it. Far more useful
                                    // than an empty "no snippet" box.
                                    <div className="space-y-1">
                                      <span className="text-xs font-medium text-muted-foreground">
                                        From this page — choose an anchor below
                                        or type one in Anchor override:
                                      </span>
                                      <div className="italic">{rowSnippet}</div>
                                    </div>
                                  ) : (
                                    <span className="text-amber-800 dark:text-amber-300">
                                      No preview available. Select it and set an
                                      anchor in Anchor override to apply.
                                    </span>
                                  )}
                                </div>
                                {candidates.length > 0 && (
                                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                    {needsAnchorSelection && (
                                      <span className="text-[10px] font-medium text-muted-foreground">
                                        Pick an anchor:
                                      </span>
                                    )}
                                    {candidates
                                      .slice(0, 3)
                                      .map((candidate, index) => {
                                        const text = anchorTextOf(candidate);
                                        const isPicked =
                                          (pickedAnchors[source.url] || "") === text;
                                        // Clickable so a row that "needs anchor
                                        // selection" can actually get one —
                                        // previously these were inert <span>s and
                                        // the only route was typing into Anchor
                                        // override, which applies to every source.
                                        return (
                                          <button
                                            key={`${text}:${index}`}
                                            type="button"
                                            title={snippetOf(candidate)}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              pickAnchor(source.url, text);
                                            }}
                                            className={`rounded border px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                                              isPicked
                                                ? "border-teal bg-teal-light text-teal-dark"
                                                : "border-border bg-muted text-foreground hover:border-teal hover:bg-teal-light/40"
                                            }`}
                                          >
                                            {text}
                                          </button>
                                        );
                                      })}
                                    {candidates.length > 3 && (
                                      <span className="px-1.5 py-0.5 text-[10px] text-muted-foreground">
                                        +{candidates.length - 3} more
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-teal-dark">
              <Pencil className="w-3 h-3" /> Anchor override
            </label>
            <input
              type="text"
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal"
              value={anchorOverride}
              onChange={(e) =>
                setDraft({
                  key: draftKey,
                  checkedUrls,
                  anchorOverride: e.target.value,
                })
              }
              placeholder="Leave blank — each source page uses its own matched phrase"
            />
          </div>
        </>
      </CardContent>
    </Card>
  );
}
